import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-133dd4e4"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "mx-5"
}
const _hoisted_2 = { class: "content mb-0" }
const _hoisted_3 = {
  key: 0,
  class: "profile-loader"
}
const _hoisted_4 = { class: "overlay" }
const _hoisted_5 = {
  key: 0,
  class: "address"
}
const _hoisted_6 = { class: "profile-address mb-6" }
const _hoisted_7 = {
  key: 1,
  class: "address profile-address-single has-text-centered mb-6"
}
const _hoisted_8 = { class: "tags" }
const _hoisted_9 = { class: "question px-2" }
const _hoisted_10 = { class: "buttons" }

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ProfilePicture from '@/popup/components/shared/ProfilePicture/ProfilePicture.vue'
import Spinner from '@/popup/components/shared/Spinner.vue'
import ImportWizard from '@/popup/components/shared/Wizard.vue'
import Button from '@/popup/components/shared/Button.vue'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { InternalMethods } from '@/shared/apis/messages'
import { useAppStore } from '@/popup/store/AppStore'
import { saveLastState } from '@/popup/storage/Database'
import { fetchLatestProfile } from '@/popup/utils/profiles'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { logError } from '@/shared/logger'
import ProfileBackground from '@/popup/components/shared/ProfileBackground/ProfileBackground.vue'
import { truncateMiddle } from '@/shared/utils'
import SvgIcon from '@/popup/components/shared/SvgIcon.vue'
import UserName from '@/popup/components/shared/Miscellaneous/UserName.vue'
import Notification from '@/popup/components/shared/Notification.vue'
import { validateUpImport } from '@/popup/utils/upProfileValidation'
import {
  FrontendProfile,
  ImportRequestInput,
  UpAddress,
} from '@/shared/models/Profile'
import { Approval, ApprovalApi } from '@/shared/apis/approvals'
import { t } from '@/popup/lib/i18n'

const ERROR_MESSAGE = 'errors.invalid_universal_profile_address'

type Emits = {
  (
    event: 'import-approval-cancel',
    approvalId: string,
    errorMessage?: string
  ): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ImportProfile',
  emits: ["import-approval-cancel"],
  setup(__props, { emit: __emit }) {

const router = useRouter()
const route = useRoute()
const approvalsStore = useApprovalsStore()
const appStore = useAppStore()
const errorMessage = ref<string>()
const submitted = ref(false)

const emits = __emit

const importRequestId = route.params['id']
  ? (route.params['id'] as string)
  : undefined

const approval: undefined | Approval<keyof ApprovalApi, unknown> =
  importRequestId ? approvalsStore.getApproval(importRequestId) : undefined

const address =
  (approval
    ? (approval?.requestData as { address: UpAddress }).address
    : (route.params['address'] as string)) || ''

const requestData =
  approval?.type === 'import'
    ? (approval?.requestData as ImportRequestInput)
    : undefined

const networkName = appStore.network.name

const profile = ref<FrontendProfile>({
  address,
  verification: {
    data: '0x01',
    method: 'keccak256(utf8)',
  },
})

const profileLoaded = ref(false)

const onSubmit = async () => {
  submitted.value = true

  await saveLastState()

  await useGlobals().sendMessage({
    method: InternalMethods.IMPORT_PROFILE,
    payload: {
      lsp23ControllerAddress: requestData?.controllerAddress,
      address,
      importRequestId,
    },
  })
  if (importRequestId) {
    approvalsStore.deleteApproval(importRequestId)
  }

  await appStore.swapProfile({
    selectedProfile: profile.value.address,
    approvals: [],
  })

  if (networkName && requestData?.controllerAddress) {
    return router.push({ path: '/home' })
  }

  const path =
    router.currentRoute.value.name === 'import-profile-dapp'
      ? '/qrcode/import-waiting'
      : '/qrcode/import-request-access'

  router.push({
    path,
    query: {
      address,
    },
  })
}

const onCancel = async () => {
  if (!approval) {
    return router.back()
  }

  emits(
    'import-approval-cancel',
    approval.id,
    requestData?.errorMessage || errorMessage.value
  )
}

const isSubmitDisabled = computed(() => {
  return (
    !profileLoaded.value ||
    submitted.value ||
    requestData?.importStatus === 'imported' ||
    !!errorMessage.value
  )
})

onMounted(async () => {
  //Validates the UP import from a dApp. dApp request land on the 'ImportProfile' screen
  //whereas the UP import from the popup starts on the 'QRCodeScan' screen.
  const currentRoute = router.currentRoute.value
  const id = currentRoute.params['id']
  if (id && currentRoute.path.startsWith('/qrcode/import-profile-dapp/')) {
    const isValidUpOrError = await validateUpImport(address)
    if (!isValidUpOrError.success) {
      errorMessage.value = t(ERROR_MESSAGE)
    }
  }
  if (requestData?.importStatus === 'imported') {
    errorMessage.value = t('errors.profile_already_exists')
  } else if (requestData?.errorMessage) {
    errorMessage.value = requestData?.errorMessage
  }

  try {
    const fetchedProfile = await fetchLatestProfile({ address })

    if (fetchedProfile) {
      profile.value = fetchedProfile
    }
  } catch (error) {
    errorMessage.value =
      error instanceof Error ? t(ERROR_MESSAGE) : 'Unknown error.'
    logError(`Error while fetching profile in ImportProfile:`, error)
  } finally {
    profileLoaded.value = true
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["import-profile-screen", { 'has-error': errorMessage.value }])
  }, [
    _createVNode(ImportWizard, {
      title: _ctx.$t('import_wizard.confirm_import'),
      progress: 40
    }, null, 8 /* PROPS */, ["title"]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(Notification, {
            type: "error",
            class: "mb-5"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(errorMessage.value), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          })
        ]))
      : _createCommentVNode("v-if", true),
    _createElementVNode("div", _hoisted_2, [
      (!profileLoaded.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(Spinner, { "box-size": 12 })
          ]))
        : _createCommentVNode("v-if", true),
      _createElementVNode("div", {
        class: _normalizeClass(["profile-content", {
          loaded: profileLoaded.value,
        }])
      }, [
        _createVNode(ProfileBackground, {
          "profile-background": profile.value?.coverPicture,
          height: 324,
          width: 360
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(ProfilePicture, {
                "frontend-profile": profile.value,
                size: "large",
                "with-identicon": true
              }, null, 8 /* PROPS */, ["frontend-profile"]),
              _createElementVNode("div", null, [
                (profile.value?.name)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(UserName, {
                        profile: profile.value,
                        "max-width": 210,
                        "has-at-sign": true,
                        "has-slice-address": true,
                        "name-class": "gradient-text",
                        class: "is-justify-content-center mb-2"
                      }, null, 8 /* PROPS */, ["profile"]),
                      _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(truncateMiddle)(profile.value.address, 8)), 1 /* TEXT */)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_unref(truncateMiddle)(profile.value?.address, 8)), 1 /* TEXT */)),
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(profile.value?.tags, (tag) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: tag,
                      class: "tag"
                    }, _toDisplayString(tag), 1 /* TEXT */))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]),
              _createElementVNode("h3", _hoisted_9, _toDisplayString(_unref(networkName) && _unref(requestData)?.controllerAddress
                  ? _unref(t)(
                      'qr_code.import_profile.existing_profile_of_another_network',
                      {
                        upAddress: _unref(truncateMiddle)(_unref(address), 8),
                        networkName: _unref(networkName),
                      }
                    )
                  : _unref(t)('qr_code.import_profile.question')), 1 /* TEXT */),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(Button, {
                  variant: "text",
                  "is-full-width": true,
                  onClick: onCancel
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('qr_code.import_profile.cancel')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }),
                _createVNode(Button, {
                  variant: "primary",
                  "is-full-width": true,
                  disabled: isSubmitDisabled.value,
                  onClick: onSubmit,
                  "data-testid": "confirm-button"
                }, {
                  default: _withCtx(() => [
                    _createVNode(SvgIcon, {
                      name: "import-profile",
                      color: "var(--neutral-100)",
                      class: "mr-2"
                    }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t(
                    errorMessage.value
                      ? 'import_waiting.confirm'
                      : 'qr_code.import_profile.confirm'
                  )), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"])
              ])
            ])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["profile-background"])
      ], 2 /* CLASS */)
    ])
  ], 2 /* CLASS */))
}
}

})